/**
 * Pure change-set helpers for the import review grid (spec §5.3.3). The undo
 * of any save is itself one `POST …/rows`, built from the rows as they were
 * just before that save — so only the previous values of what the save
 * touched are kept, never a full snapshot of the batch.
 */
import type { ImportBatchRow, RosterResolutionAction, RowChangeSet, RowChoice } from '@discovr/types';

type RowEdit = RowChangeSet['edits'][number];
type RowDecision = RowChangeSet['decisions'][number];

// Mirrors the ui package's `rowChoice`; kept local because `features` never
// imports `ui`. `add` never sits on a matched row and `skip` is the server's
// auto-identical marker (decisions on it are rejected), so neither can be the
// "previous choice" of a real decision — both fall back to undecided.
const CHOICE_FOR_RESOLUTION: Record<RosterResolutionAction, RowChoice> = {
  overwrite: 'sheet',
  keep_existing: 'current',
  '': 'undecided',
  add: 'undecided',
  skip: 'undecided',
};

const AUTO_IDENTICAL_RESOLUTION: RosterResolutionAction = 'skip';
const USER_DELETED_REASON: ImportBatchRow['dropReason'] = 'deleted';
const EDITED_RESOLUTION: ImportBatchRow['sheetResolution'] = 'fixed';
const DECIDED_RESOLUTIONS: ReadonlySet<RosterResolutionAction> = new Set(['overwrite', 'keep_existing']);

export function emptyChangeSet(): RowChangeSet {
  return { edits: [], deletes: [], restores: [], decisions: [] };
}

function indexRows(rows: ImportBatchRow[]): Map<number, ImportBatchRow> {
  return new Map(rows.map((row) => [row.rowNumber, row]));
}

function previousValuesOf(row: ImportBatchRow, keys: string[]): Record<string, string> {
  return Object.fromEntries(keys.map((key) => [key, row.values[key] ?? '']));
}

function undoEdit(rowsByNumber: Map<number, ImportBatchRow>, edit: RowEdit): RowEdit | null {
  const before = rowsByNumber.get(edit.rowNumber);
  if (!before) return null;
  return { rowNumber: edit.rowNumber, values: previousValuesOf(before, Object.keys(edit.values)) };
}

function undoDecision(rowsByNumber: Map<number, ImportBatchRow>, decision: RowDecision): RowDecision | null {
  const before = rowsByNumber.get(decision.rowNumber);
  if (!before) return null;
  return { rowNumber: decision.rowNumber, choice: CHOICE_FOR_RESOLUTION[before.rosterResolution] };
}

function isPresent<T>(value: T | null): value is T {
  return value !== null;
}

export function isEmptyChangeSet(changes: RowChangeSet): boolean {
  return changes.edits.length + changes.deletes.length + changes.restores.length + changes.decisions.length === 0;
}

/** Undoing a restore re-deletes the row, which only matches what it was when
 *  the admin had deleted it: an exact copy or a skipped row would come back
 *  as "deleted" and drop out of every tab. Those restores get no undo. */
function isUndoableRestore(rowsByNumber: Map<number, ImportBatchRow>, rowNumber: number): boolean {
  return rowsByNumber.get(rowNumber)?.dropReason === USER_DELETED_REASON;
}

export function buildUndoChangeSet(rowsBefore: ImportBatchRow[], changes: RowChangeSet): RowChangeSet {
  const rowsByNumber = indexRows(rowsBefore);
  return {
    edits: changes.edits.map((edit) => undoEdit(rowsByNumber, edit)).filter(isPresent),
    deletes: changes.restores.filter((rowNumber) => isUndoableRestore(rowsByNumber, rowNumber)),
    restores: [...changes.deletes],
    decisions: changes.decisions.map((decision) => undoDecision(rowsByNumber, decision)).filter(isPresent),
  };
}

function isDropped(row: ImportBatchRow): boolean {
  return row.dropReason !== '' || row.sheetResolution === 'drop';
}

function isDecidableMatch(row: ImportBatchRow): boolean {
  if (row.rosterMatch === null) return false;
  if (isDropped(row)) return false;
  return row.rosterResolution !== AUTO_IDENTICAL_RESOLUTION;
}

/** One decision per existing creator the admin can still choose for — the
 *  bulk "Keep all current" / "Use sheet for all" / "Change" actions. */
export function decisionsForAll(rows: ImportBatchRow[], choice: RowChoice): RowChangeSet['decisions'] {
  return rows.filter(isDecidableMatch).map((row) => ({ rowNumber: row.rowNumber, choice }));
}

function rowShowsSavedChange(row: ImportBatchRow): boolean {
  if (row.sheetResolution === EDITED_RESOLUTION) return true;
  if (row.dropReason === USER_DELETED_REASON) return true;
  return DECIDED_RESOLUTIONS.has(row.rosterResolution);
}

/** Whether a batch already carries the admin's own edits, deletes or
 *  decisions — used when resuming a draft, where no save happened in this
 *  session but going Back to mapping would still discard earlier work. */
export function rowsHaveSavedChanges(rows: ImportBatchRow[]): boolean {
  return rows.some(rowShowsSavedChange);
}
